// Modern Lumoz Console with clean Stytch B2B integration
// Uses pure StytchB2BProvider architecture - no custom auth wrappers

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';
import ProtectedRoute from './components/ProtectedRoute';
import StaleAppGuard from './components/StaleAppGuard';
import ConsoleLayout from './components/ConsoleLayout';
import Settings from './pages/Settings';
import Policies from './pages/Policies';
import Sessions from './pages/Sessions';
import Traces from './pages/Traces';
import TraceDetail from './pages/TraceDetail';
import SessionDetail from './pages/SessionDetail';
import Agents from './pages/Agents';
import Home from './pages/Home';
import AppDetails from './pages/AppDetails';
import SignalInvestigationPage from './pages/SignalInvestigationPage';
import Licenses from './pages/Licenses';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { TimeRangeProvider } from './contexts/TimeRangeContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { AppProvider } from './contexts/AppContext';

console.log('🔄 CONSOLE: Fresh component loaded with modern Stytch architecture');

// Initialize Stytch B2B client
console.log('🔍 CONSOLE: Initializing Stytch B2B client...');
const publicToken = process.env.REACT_APP_STYTCH_PUBLIC_TOKEN;
if (!publicToken) {
  throw new Error('REACT_APP_STYTCH_PUBLIC_TOKEN is required');
}
const stytchClient = new StytchB2BUIClient(publicToken);
console.log('✅ CONSOLE: Stytch client initialized:', !!stytchClient);

// Simple home page component
const HomePage = () => {
  console.log('🔍 HOME: Rendering home page');
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-text-primary">Welcome to Lumoz Console</h1>
      <p className="text-text-secondary mt-4">Your AI console is ready to build!</p>
    </div>
  );
};

// Redirect component that preserves query parameters
const RedirectToHome = () => {
  const location = useLocation();
  return <Navigate to={`/home${location.search}`} replace />;
};

// Simplified router - Magic link authentication only
const AppRouter = () => {
  console.log('🔍 APP-ROUTER: Determining route...');
  
  console.log('🔄 APP-ROUTER: Rendering protected console');
  return (
    <ProtectedRoute>
      <ConsoleLayout>
        <StaleAppGuard />
        <Routes>
          <Route path="/" element={<RedirectToHome />} />
          <Route path="/home" element={<Home />} />

          {/* App-scoped routes */}
          <Route path="/app/:selectedAppId" element={<AppDetails />} />
          <Route path="/app/:selectedAppId/signals/:classifierKey" element={<SignalInvestigationPage />} />
          <Route path="/app/:selectedAppId/traces" element={<Traces />} />
          <Route path="/app/:selectedAppId/traces/:traceId" element={<TraceDetail />} />
          <Route path="/app/:selectedAppId/sessions" element={<Sessions />} />
          <Route path="/app/:selectedAppId/sessions/:sessionId" element={<SessionDetail />} />
          <Route path="/app/:selectedAppId/agents" element={<Agents />} />

          <Route path="/policies" element={<Policies />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/licenses" element={<Licenses />} />
          <Route path="/authenticate" element={<Navigate to="/home" replace />} />
        </Routes>
      </ConsoleLayout>
    </ProtectedRoute>
  );
};

// Main Console component with pure Stytch architecture
function Console() {
  console.log('🚀 CONSOLE: Main component rendering...');
  console.log('🔍 CONSOLE: Using pure StytchB2BProvider architecture');
  console.log('🔍 CONSOLE: No custom auth wrappers - clean Stytch integration');

  // Stytch event callback for tracking magic link flow
  const handleStytchEvent = (event) => {
    console.log('🔍 STYTCH-EVENT:', event.type, event);
    
    switch (event.type) {
      case 'B2B_MAGIC_LINK_EMAIL_LOGIN_OR_SIGNUP':
        console.log('✅ STYTCH-EVENT: Magic link email sent successfully');
        break;
      case 'B2B_MAGIC_LINK_AUTHENTICATE':
        console.log('✅ STYTCH-EVENT: Magic link authentication completed');
        console.log('🔍 STYTCH-EVENT: Member ID:', event.details?.member_id);
        console.log('🔍 STYTCH-EVENT: Organization ID:', event.details?.organization_id);
        break;
      default:
        console.log('🔍 STYTCH-EVENT: Other event type:', event.type);
    }
  };

  // Stytch error callback for handling authentication errors
  const handleStytchError = (error) => {
    console.error('❌ STYTCH-ERROR:', error);
    console.error('🔍 STYTCH-ERROR: Error type:', error.error_type);
    console.error('🔍 STYTCH-ERROR: Error message:', error.error_message);
    
    // Could add user-friendly error handling here
    // For now, just log detailed error information
  };

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <StytchB2BProvider
          stytch={stytchClient}
          onEvent={handleStytchEvent}
          onError={handleStytchError}
        >
          <Router>
            <TimeRangeProvider>
              <EnvironmentProvider>
                <AppProvider>
                  <AppRouter />
                </AppProvider>
              </EnvironmentProvider>
            </TimeRangeProvider>
          </Router>
        </StytchB2BProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

console.log('✅ CONSOLE: Component module ready');

export default Console;
